// Очередь неотправленных команд протокола (015: research, Р5; FR-017, FR-019, FR-020, FR-022;
// COLLAB-08, COLLAB-10). Сформированная команда пишется в хранилище до отправки и удаляется после
// ответа сервера (принята, повтор, отказ); обрыв и окончание сеанса запись не трогают — повтор
// уходит тем же номером и телом. При открытии протокола записи пользователя читаются по порядку:
// команды текущего поколения уходят первыми; другого поколения, протокола в режиме файла, старше
// 7 дней и сверх 200 — не отправляются: правки ячеек становятся черновиками 012, остальные
// команды (дерево, пакет, поповеры) удаляются с уведомлением. Записи другого пользователя этого
// браузера не читаются и не отправляются.
//
// Две вкладки одного пользователя (допущение 015): чужие записи вкладка принимает, только держа
// замок протокола (navigator.locks); команды, что вкладка сама отправляет, помечены в памяти
// вкладки и при восстановлении пропускаются. Двойная отправка всё равно безопасна: повтор того же
// номера с тем же телом сервер не применяет второй раз (replayed).
import type { Command } from '@kp/contracts';
import type { DraftStore } from '../drafts.ts';
import type { StoredIntent } from './intents.ts';
import { keyOf, queueStore } from './store.ts';
import type { QueueKey, QueueRecord, QueueStore } from './store.ts';

/** Предел записей протокола (data-model.md): старше — черновиками. */
export const QUEUE_LIMIT = 200;
/** Срок записи — 7 дней, как у черновиков 012. */
export const QUEUE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Почему команда не отправлена при восстановлении. */
export type StaleReason = 'generation' | 'reverted' | 'expired';

export interface StaleCommand {
  readonly command?: Command;
  readonly intent?: StoredIntent;
  readonly reason: StaleReason;
}

export interface Restored {
  /** Formed commands and waiting intents in their original order. */
  readonly entries: readonly ({ command: Command } | { intent: StoredIntent })[];
  /** Команды текущего поколения по порядку: уходят первыми тем же номером и телом. */
  readonly resend: readonly Command[];
  /** Не отправляются: черновики или удаление с уведомлением (toDrafts). */
  readonly stale: readonly StaleCommand[];
}

/** Команды, которые сейчас ведёт эта вкладка: восстановление их пропускает. */
const claimed = new Set<string>();

/** Перезагрузка страницы в тестах: вкладка больше не ведёт ни одной команды. */
export function forgetQueueClaims(): void {
  claimed.clear();
}

let lastSeq = 0;

/** Номер по порядку: время создания, а при совпадении — следующий. */
function nextSeq(now: number): number {
  lastSeq = Math.max(now, lastSeq + 1);
  return lastSeq;
}

export interface QueueScope {
  readonly userId: string;
  readonly documentId: string;
}

export class CommandQueue {
  private readonly store: QueueStore;
  private readonly scope: QueueScope;
  private readonly now: () => number;
  /** Ключи записей этой очереди по номеру команды. */
  private readonly keys = new Map<string, QueueRecord>();
  private writes: Promise<void> = Promise.resolve();

  private write(run: () => Promise<void>): Promise<void> {
    this.writes = this.writes.then(run).catch(() => undefined);
    return this.writes;
  }

  constructor(scope: QueueScope, store: QueueStore = queueStore(), now: () => number = Date.now) {
    this.scope = scope;
    this.store = store;
    this.now = now;
  }

  /** Хранилище браузера недоступно: очередь в памяти вкладки, перезагрузку не переживёт. */
  get unavailable(): boolean {
    return !this.store.durable;
  }

  /** Запись до отправки. Сбой хранилища не останавливает отправку — только предупреждение. */
  async record(
    command: Command,
    transferRevision: number,
    superseded: readonly string[] = [],
  ): Promise<void> {
    const replaced = superseded.flatMap((id) =>
      this.keys.has(id) ? [keyOf(this.keys.get(id)!)] : [],
    );
    for (const id of superseded) {
      this.keys.delete(id);
      claimed.delete(id);
    }
    const createdAt = this.now();
    const record: QueueRecord = {
      userId: this.scope.userId,
      documentId: this.scope.documentId,
      transferRevision,
      seq: this.keys.get(command.commandId)?.seq ?? nextSeq(createdAt),
      ...(this.keys.get(command.commandId)?.orderPath
        ? { orderPath: this.keys.get(command.commandId)!.orderPath }
        : {}),
      commandId: command.commandId,
      body: command,
      baseRevision: command.baseRevision,
      createdAt,
      attempts: 0,
    };
    this.keys.set(command.commandId, record);
    claimed.add(command.commandId);
    try {
      await this.write(() => this.store.put(record, replaced));
    } catch {
      // Хранилище отказало: команда уйдёт и без записи.
    }
  }

  /** Persist a queued intent immediately; its body/base are chosen only at send time. */
  async defer(intent: StoredIntent, transferRevision: number, afterId?: string): Promise<void> {
    const previous = this.keys.get(intent.commandId);
    const after = afterId ? this.keys.get(afterId) : undefined;
    const createdAt = previous?.createdAt ?? this.now();
    const seq = previous?.seq ?? nextSeq(createdAt);
    const orderPath =
      previous?.orderPath ?? (after ? [...(after.orderPath ?? [after.seq]), seq] : [seq]);
    const record: QueueRecord = {
      ...this.scope,
      transferRevision,
      seq,
      orderPath,
      commandId: intent.commandId,
      intent: structuredClone(intent),
      baseRevision: 0,
      createdAt,
      attempts: 0,
    };
    this.keys.set(intent.commandId, record);
    claimed.add(intent.commandId);
    await this.write(() => this.store.put(record));
  }

  /** Повтор той же команды: число попыток в записи (номер и тело — прежние). */
  async touch(commandId: string): Promise<void> {
    const record = this.keys.get(commandId);
    if (!record) return;
    const next = { ...record, attempts: record.attempts + 1 };
    this.keys.set(commandId, next);
    try {
      await this.write(() => this.store.put(next));
    } catch {
      // Число попыток — справка; команда остаётся в очереди.
    }
  }

  /** Сервер ответил (принята, повтор или отказ): записи больше нет. */
  async remove(commandId: string): Promise<void> {
    const record = this.keys.get(commandId);
    this.keys.delete(commandId);
    claimed.delete(commandId);
    if (!record) return;
    try {
      await this.write(() => this.store.delete(keyOf(record)));
    } catch {
      // Не удалилась — при следующем открытии уйдёт повтором, сервер ответит replayed.
    }
  }

  /**
   * Вкладка больше не ведёт команду (сессию закрыли, пока она ждала повтора): запись остаётся,
   * её возьмёт следующее открытие протокола.
   */
  release(commandId: string): void {
    this.keys.delete(commandId);
    claimed.delete(commandId);
  }

  /**
   * Записи пользователя и протокола при открытии: текущего поколения — к отправке по порядку,
   * остальные удаляются из хранилища и возвращаются как невыполнимые.
   */
  async restore(current: {
    transferRevision: number;
    mode: 'active' | 'reverted';
  }): Promise<Restored> {
    let records: QueueRecord[];
    try {
      await this.writes;
      records = await this.store.list(this.scope.userId, this.scope.documentId);
    } catch {
      return { entries: [], resend: [], stale: [] };
    }
    const now = this.now();
    const stale: { record: QueueRecord; reason: StaleReason }[] = [];
    const fresh: QueueRecord[] = [];
    for (const record of records) {
      if (claimed.has(record.commandId)) continue;
      if (current.mode === 'reverted') stale.push({ record, reason: 'reverted' });
      else if (record.transferRevision !== current.transferRevision) {
        stale.push({ record, reason: 'generation' });
      } else if (now - record.createdAt > QUEUE_TTL_MS) stale.push({ record, reason: 'expired' });
      else fresh.push(record);
    }
    fresh.sort((a, b) => {
      const left = a.orderPath ?? [a.seq];
      const right = b.orderPath ?? [b.seq];
      for (let index = 0; index < Math.min(left.length, right.length); index++) {
        const delta = left[index]! - right[index]!;
        if (delta !== 0) return delta;
      }
      return left.length - right.length || a.seq - b.seq;
    });
    // Сверх предела — самые старые: отправка новых не блокируется.
    const over = Math.max(0, fresh.length - QUEUE_LIMIT);
    for (const record of fresh.splice(0, over)) stale.push({ record, reason: 'expired' });
    for (const { record } of stale) {
      try {
        await this.write(() => this.store.delete(keyOf(record)));
      } catch {
        // Не удалилась — в следующий раз снова станет черновиком.
      }
    }
    for (const record of fresh) {
      this.keys.set(record.commandId, record);
      claimed.add(record.commandId);
    }
    return {
      entries: fresh.flatMap<{ command: Command } | { intent: StoredIntent }>((record) =>
        record.body ? [{ command: record.body }] : record.intent ? [{ intent: record.intent }] : [],
      ),
      resend: fresh.flatMap((record) => (record.body ? [record.body] : [])),
      stale: stale.map(({ record, reason }) => ({
        ...(record.body ? { command: record.body } : { intent: record.intent! }),
        reason,
      })),
    };
  }

  /**
   * Протокол вернули в режим файла или перевели заново, пока таблица открыта (FR-022): записи
   * протокола, которые вкладка не ведёт сейчас, не уйдут — удаляются и возвращаются. Новый
   * перевод ('generation') — только записи прежнего поколения вкладки (transferRevision): записи
   * другой вкладки, открытой уже на новом поколении, уйдут из неё (T039).
   */
  async retire(reason: StaleReason, transferRevision: number): Promise<StaleCommand[]> {
    let records: QueueRecord[];
    try {
      await this.writes;
      records = await this.store.list(this.scope.userId, this.scope.documentId);
    } catch {
      return [];
    }
    const stale: StaleCommand[] = [];
    for (const record of records) {
      if (claimed.has(record.commandId)) continue;
      if (reason === 'generation' && record.transferRevision !== transferRevision) continue;
      this.keys.delete(record.commandId);
      stale.push({
        ...(record.body ? { command: record.body } : { intent: record.intent! }),
        reason,
      });
      try {
        await this.write(() => this.store.delete(keyOf(record)));
      } catch {
        // Не удалилась — станет черновиком при следующем открытии.
      }
    }
    return stale;
  }

  /**
   * Открытая таблица сообщает, сколько у неё правок, которых ещё нет в хранилище (окно выхода);
   * итог — перестать сообщать.
   */
  track(source: () => number): () => void {
    const sources = unformedSources.get(this.scope.userId) ?? new Set<() => number>();
    sources.add(source);
    unformedSources.set(this.scope.userId, sources);
    return () => {
      sources.delete(source);
    };
  }

  /** «Отменить несохранённые правки»: все записи протокола этого пользователя (FR-023). */
  async clear(): Promise<void> {
    let records: QueueRecord[];
    try {
      await this.writes;
      records = await this.store.list(this.scope.userId, this.scope.documentId);
    } catch {
      return;
    }
    for (const record of records) {
      this.keys.delete(record.commandId);
      claimed.delete(record.commandId);
      try {
        await this.write(() => this.store.delete(keyOf(record)));
      } catch {
        // Не удалилась — повтор при следующем открытии; принятая команда второго эффекта не даст.
      }
    }
  }
}

/**
 * Невыполнимые команды: правки ячеек (cells.edit) — черновики 012 с «Вернуть мой текст»;
 * остальные удаляются — их тело не правка одной ячейки (решение основной сессии 24.10.2026).
 */
export function toDrafts(
  stale: readonly StaleCommand[],
  drafts: DraftStore,
): { drafts: number; dropped: number } {
  let drafted = 0;
  let dropped = 0;
  for (const { command, intent } of stale) {
    if (intent?.kind === 'cells') {
      for (const [key, edit] of intent.edits ?? []) {
        const [itemId, field] = key.split('|') as [string, string];
        drafts.save(itemId, field, edit.value);
        drafted += 1;
      }
      continue;
    }
    if (!command || command.kind !== 'cells.edit') {
      dropped += 1;
      continue;
    }
    for (const edit of command.edits) {
      drafts.save(edit.itemId, edit.field, edit.value);
      drafted += 1;
    }
  }
  return { drafts: drafted, dropped };
}

/** Открытые таблицы пользователя: правки, которых ещё нет в хранилище (не стали командой). */
const unformedSources = new Map<string, Set<() => number>>();

/**
 * Число неотправленных правок пользователя во всех протоколах — для окна выхода (FR-021):
 * команды в хранилище и правки открытых таблиц этой вкладки, которые ещё не стали командой.
 */
export async function unsentOfUser(
  userId: string,
  store: QueueStore = queueStore(),
): Promise<number> {
  let open = 0;
  for (const source of unformedSources.get(userId) ?? []) open += source();
  try {
    return (await store.list(userId)).length + open;
  } catch {
    return open;
  }
}

/** Выход из системы подтверждён: записи пользователя удаляются (ADR-0032; FR-021). */
export async function dropUserQueue(
  userId: string,
  store: QueueStore = queueStore(),
): Promise<void> {
  let records: QueueRecord[];
  try {
    records = await store.list(userId);
  } catch {
    return;
  }
  for (const record of records) {
    claimed.delete(record.commandId);
    try {
      await store.delete(keyOf(record));
    } catch {
      // Хранилище отказало — запись уйдёт со следующим выходом.
    }
  }
}

/**
 * Замок очереди протокола вкладки (navigator.locks): итог — отпустить замок. wait = false — только
 * если замок свободен сейчас, иначе null (другая вкладка этого пользователя держит протокол);
 * wait = true — дождаться. Без navigator.locks (старый браузер, тесты) — сразу.
 */
export function holdQueueLock(scope: QueueScope, wait: boolean): Promise<(() => void) | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return Promise.resolve(() => undefined);
  return new Promise((resolve) => {
    void locks
      .request(
        `kp-minutes-queue:${scope.userId}:${scope.documentId}`,
        { ifAvailable: !wait },
        (lock) => {
          if (!lock) {
            resolve(null);
            return undefined;
          }
          return new Promise<void>((release) => resolve(() => release()));
        },
      )
      .catch(() => resolve(() => undefined));
  });
}

export type { QueueKey, QueueRecord, QueueStore };
